import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ReactMarkdown from "react-markdown";
import {
  ArrowLeft, CheckCircle2, XCircle, Save, Loader2, BookOpen, Lightbulb, Zap, Quote,
} from "lucide-react";

/**
 * AdminLessonDetailPage — side-by-side editor for a single draft.
 * Left: editable form. Right: live preview as students will see it.
 */
const AdminLessonDetailPage = () => {
  const { draftId } = useParams();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [estTime, setEstTime] = useState(15);
  const [sections, setSections] = useState([]);
  const [keyTakeaways, setKeyTakeaways] = useState([]);
  const [examTips, setExamTips] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get(`/admin/lessons/drafts/${draftId}`);
      setDraft(d);
      setName(d.name || "");
      setVideoUrl(d.video_url || "");
      setEstTime(d.estimated_time || 15);
      setSections(d.sections || []);
      setKeyTakeaways(d.key_takeaways || []);
      setExamTips(d.exam_tips || []);
    } catch (e) {
      toast.error("Failed to load draft");
    } finally {
      setLoading(false);
    }
  }, [draftId]);

  useEffect(() => { load(); }, [load]);

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/lessons/drafts/${draftId}`, {
        name, video_url: videoUrl || null, estimated_time: estTime,
        sections, key_takeaways: keyTakeaways, exam_tips: examTips,
      });
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const approveDraft = async () => {
    if (!window.confirm("Publish this lesson into the Learn section?")) return;
    setSaving(true);
    try {
      // Auto-save first
      await api.put(`/admin/lessons/drafts/${draftId}`, {
        name, video_url: videoUrl || null, estimated_time: estTime,
        sections, key_takeaways: keyTakeaways, exam_tips: examTips,
      });
      await api.post(`/admin/lessons/drafts/${draftId}/approve`, {});
      toast.success("Published to Learn. Students see it now.");
      navigate("/admin/lessons-review");
    } catch (e) {
      toast.error("Approve failed");
      setSaving(false);
    }
  };

  const rejectDraft = async () => {
    if (!window.confirm("Reject this draft? It will be hidden from the queue.")) return;
    try {
      await api.post(`/admin/lessons/drafts/${draftId}/reject`, {});
      toast.success("Rejected");
      navigate("/admin/lessons-review");
    } catch (e) {
      toast.error("Reject failed");
    }
  };

  const updateSection = (idx, field, value) => {
    setSections(sections.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const removeSection = (idx) => {
    if (!window.confirm("Remove this section?")) return;
    setSections(sections.filter((_, i) => i !== idx));
  };

  const addSection = () => setSections([...sections, { title: "New section", content: "" }]);

  const updateListItem = (list, setList) => (idx, value) => {
    setList(list.map((item, i) => i === idx ? value : item));
  };

  const removeListItem = (list, setList) => (idx) => {
    setList(list.filter((_, i) => i !== idx));
  };

  if (loading || !draft) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-lesson-detail-page" className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={() => navigate("/admin/lessons-review")}
          className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          data-testid="admin-lesson-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to queue
        </button>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" onClick={rejectDraft} disabled={saving}
            data-testid="admin-lesson-reject" className="rounded-full"
          >
            <XCircle className="w-4 h-4 mr-1" /> Reject
          </Button>
          <Button
            variant="outline" size="sm" onClick={saveDraft} disabled={saving}
            data-testid="admin-lesson-save" className="rounded-full"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
          <Button
            size="sm" onClick={approveDraft} disabled={saving}
            data-testid="admin-lesson-approve" className="rounded-full"
          >
            <CheckCircle2 className="w-4 h-4 mr-1" /> Approve & publish
          </Button>
        </div>
      </div>

      <div className="text-xs text-muted-foreground">
        <span className="font-medium">{draft.subject_id}</span>
        {" · topic_id "}<code className="bg-secondary px-1 rounded">{draft.topic_id}</code>
        {" · v"}{draft.version} · <span className="capitalize">{draft.status}</span>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* LEFT — Editor */}
        <div className="space-y-4">
          <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Editor</div>

          <FormField label="Topic name">
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              data-testid="admin-lesson-name" className="form-input" />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Estimated time (minutes)">
              <input
                type="number" min={5} max={60} value={estTime}
                onChange={(e) => setEstTime(parseInt(e.target.value || "15", 10))}
                data-testid="admin-lesson-time" className="form-input" />
            </FormField>
            <FormField label="Video URL (optional)">
              <input
                type="url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://youtube.com/..." data-testid="admin-lesson-video"
                className="form-input" />
            </FormField>
          </div>

          {/* Sections */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Sections ({sections.length})</div>
              <button onClick={addSection} className="text-xs text-primary font-medium" data-testid="admin-lesson-add-section">+ Add section</button>
            </div>
            {sections.map((sec, idx) => (
              <div key={idx} className="rounded-lg border border-border p-3 space-y-2 bg-card">
                <input
                  type="text" value={sec.title || ""}
                  onChange={(e) => updateSection(idx, "title", e.target.value)}
                  data-testid={`admin-lesson-section-title-${idx}`}
                  className="form-input font-medium" placeholder="Section title" />
                <textarea
                  value={sec.content || ""}
                  onChange={(e) => updateSection(idx, "content", e.target.value)}
                  rows={6} data-testid={`admin-lesson-section-content-${idx}`}
                  className="form-input font-mono text-xs leading-relaxed"
                  placeholder="Markdown content..." />
                <button
                  onClick={() => removeSection(idx)}
                  className="text-xs text-destructive hover:underline"
                  data-testid={`admin-lesson-section-remove-${idx}`}
                >
                  Remove section
                </button>
              </div>
            ))}
          </div>

          {/* Key takeaways */}
          <EditableList
            label="Key takeaways"
            items={keyTakeaways}
            setItems={setKeyTakeaways}
            updater={updateListItem(keyTakeaways, setKeyTakeaways)}
            remover={removeListItem(keyTakeaways, setKeyTakeaways)}
            testidPrefix="admin-lesson-takeaway"
          />
          <EditableList
            label="Exam tips"
            items={examTips}
            setItems={setExamTips}
            updater={updateListItem(examTips, setExamTips)}
            remover={removeListItem(examTips, setExamTips)}
            testidPrefix="admin-lesson-tip"
          />

          {/* Sources */}
          {(draft.sources || []).length > 0 && (
            <div className="rounded-lg border border-border p-3 bg-secondary/30">
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">Sources cited</div>
              <ul className="text-xs space-y-1">
                {draft.sources.map((src, i) => (
                  <li key={i} className="flex gap-2"><Quote className="w-3 h-3 flex-shrink-0 mt-0.5 text-muted-foreground" /><span>{src}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* RIGHT — Preview */}
        <div className="space-y-4 lg:sticky lg:top-6 self-start">
          <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Student preview</div>
          <div className="glass rounded-2xl p-5 border border-border max-h-[calc(100vh-12rem)] overflow-y-auto">
            <div className="text-xs text-primary font-semibold mb-2">{estTime} min · {sections.length} sections</div>
            <h2 className="text-xl font-bold mb-4">{name}</h2>
            {videoUrl && (
              <div className="mb-4 text-xs text-muted-foreground">
                📺 Video attached: <a href={videoUrl} target="_blank" rel="noreferrer" className="text-primary underline">{videoUrl}</a>
              </div>
            )}
            {sections.map((sec, idx) => (
              <div key={idx} className="mb-5">
                <h3 className="text-base font-semibold mb-2 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-primary" />
                  {sec.title}
                </h3>
                <div className="prose prose-sm max-w-none text-sm leading-relaxed">
                  <ReactMarkdown
                    components={{
                      // eslint-disable-next-line react/no-unstable-nested-components
                      strong: ({ children }) => <strong className="text-primary font-semibold">{children}</strong>,
                      // eslint-disable-next-line react/no-unstable-nested-components
                      ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>,
                      // eslint-disable-next-line react/no-unstable-nested-components
                      p: ({ children }) => <p className="mb-3">{children}</p>,
                    }}
                  >
                    {sec.content || ""}
                  </ReactMarkdown>
                </div>
              </div>
            ))}
            {keyTakeaways.length > 0 && (
              <div className="mt-6 rounded-xl bg-primary/5 border border-primary/20 p-4">
                <div className="text-sm font-semibold text-primary mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" /> Key takeaways
                </div>
                <ul className="space-y-1.5 text-sm">
                  {keyTakeaways.map((k, i) => <li key={i}>· {k}</li>)}
                </ul>
              </div>
            )}
            {examTips.length > 0 && (
              <div className="mt-4 rounded-xl bg-amber-500/10 border border-amber-500/30 p-4">
                <div className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4" /> Exam tips
                </div>
                <ul className="space-y-1.5 text-sm">
                  {examTips.map((t, i) => <li key={i}>· {t}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const FormField = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">{label}</span>
    {children}
  </label>
);

const EditableList = ({ label, items, setItems, updater, remover, testidPrefix }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <div className="text-sm font-semibold">{label} ({items.length})</div>
      <button
        onClick={() => setItems([...items, ""])}
        className="text-xs text-primary font-medium"
        data-testid={`${testidPrefix}-add`}
      >+ Add</button>
    </div>
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx} className="flex gap-2">
          <input
            type="text" value={item} onChange={(e) => updater(idx, e.target.value)}
            data-testid={`${testidPrefix}-${idx}`} className="form-input flex-1 text-sm" />
          <button
            onClick={() => remover(idx)}
            data-testid={`${testidPrefix}-remove-${idx}`}
            className="px-2 text-xs text-destructive hover:bg-destructive/10 rounded-md"
          >×</button>
        </div>
      ))}
    </div>
  </div>
);

export default AdminLessonDetailPage;
